import React from 'react';
import { Image } from '@adobe/react-spectrum';
import moment from 'moment';
import classNames from 'classnames';
import MaximizeIcon from 'app/assets/svgIcons/MaximizeIcon';
import MinimizeIcon from 'app/assets/svgIcons/MinimizeIcon';
import { getCategoryIcon } from 'utils/categoryIconUtils';
import Header from 'app/components/SlideOutPanel/Header';
import { RBACProvider } from 'app/components/SlideOutPanel/context/RBACContext';
import { FeedbackSlideoutContext } from '../types';
import styles from './FeedbackSlideOutHeader.module.scss';

interface FeedbackSlideOutHeaderProps {
  context: FeedbackSlideoutContext;
  onClose: () => void;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  mobileLayout?: boolean;
}

/**
 * Feedback slide-out's own header - this renders the actual `Header`
 * component the Release Moment slide-out itself uses
 * (SlideOutPanel/Header/index.tsx), not a rebuilt lookalike. Two prior
 * attempts at a hand-built header (borrowing only CSS classes) were
 * rejected for exactly that reason - this is the real component.
 *
 * `Header` already renders in a fully read-only mode (no title-rename, no
 * product/GenAI/Consumes-Credit dropdown editing, no PAR ID popup) when
 * its RBAC permission data is null. A header-local `RBACProvider` enforces
 * that existing "view only" mode even when Feedback opens inside an editable
 * RSD slide-out, without changing permissions for Notes and Feedback.
 * `issueId`/`parId`/`onExportPdf` are
 * omitted, which (via Header's own existing conditionals) hides
 * Clone/Share/PAR-ID/Export, none of which apply here or appear in the
 * Feedback slide-out's Figma. `onNavigatePrevious`/`onNavigateNext` are
 * omitted too, which (via Header's own `isDisabled={!onNavigatePrevious}`)
 * disables the chevrons the same way Header already disables them for the
 * Moment slide-out's own first/last item - not a separate disabled state
 * invented for this panel.
 *
 * `headerRightExtra` is a new, purely additive optional prop added to
 * `Header` itself (see its own doc comment) specifically for this
 * feature's Milestone-name/Due-Date pills, since Header has no other slot
 * for them - every existing caller (the real Moment slide-out) simply
 * never passes it, so its own rendering is unchanged. `readOnlyChips` is
 * the same kind of additive prop, added to hide the GenAI/Consumes-Credit
 * pills' dropdown chevron - those two otherwise always show it once the
 * moment has a value for them, regardless of edit permission (only the
 * click-to-open behavior is permission-gated), which reads as editable
 * even though it never was for a view-only render like this one.
 *
 * `onNavigatePrevious`/`onNavigateNext` come from the table that opened
 * this panel (see FeedbackSlideoutNavigation in ../types.ts) - each table
 * builds these itself from its own currently-displayed row order, the
 * same "does a sibling exist, if so provide a callback, else omit it"
 * shape Header's own chevrons already expect (and already render disabled
 * for, via its existing `isDisabled={!onNavigatePrevious}`) for the
 * Moment slide-out's own first/last item.
 *
 * The Due-on pill's red styling only applies when `context.isOverdue` is
 * true (taken as-is from the row's own already-computed overdue state, not
 * recalculated here); otherwise it renders with `.dueDatePillNeutral`/
 * `.dueDatePillTextNeutral`, the exact same neutral chip colors (`#f8f8f8`
 * background / `#464646` text) Header's own Surfaces pill (`.pill`/
 * `.pillText`, Header.module.scss) already uses for "Marketing Moment"/
 * "Desktop" - reused rather than a third color introduced for this pill.
 */
export function FeedbackSlideOutHeader({
  context,
  onClose,
  isFullScreen,
  onToggleFullScreen,
  onNavigatePrevious,
  onNavigateNext,
  mobileLayout = false
}: FeedbackSlideOutHeaderProps) {
  const { momentName, milestoneName, dueDate, isOverdue, releaseMoment } =
    context;

  return (
    <RBACProvider permissionData={null}>
      <Header
        title={momentName}
        onClose={onClose}
        categories={releaseMoment.categories}
        productIcon={
          <Image
            data-testid="feedback-slideout-product-icon"
            src={getCategoryIcon(
              releaseMoment.categories,
              releaseMoment.products
            )}
            alt=""
            width="32px"
            height="32px"
          />
        }
        products={releaseMoment.products}
        productGroups={releaseMoment.productGroups}
        momentType={releaseMoment.momentType}
        surfaces={releaseMoment.surfaces}
        genai={releaseMoment.genai}
        consumesCredit={releaseMoment.consumesCredit}
        readOnlyChips
        mobileLayout={mobileLayout}
        isFullScreen={isFullScreen}
        onMaximize={onToggleFullScreen}
        MaximizeIcon={isFullScreen ? MinimizeIcon : MaximizeIcon}
        onNavigatePrevious={onNavigatePrevious}
        onNavigateNext={onNavigateNext}
        headerRightExtra={
          <div
            className={classNames(styles.rightExtra, {
              [styles.rightExtraMobile]: mobileLayout
            })}
          >
            <div className={styles.milestonePill}>
              <span className={styles.milestonePillText}>{milestoneName}</span>
            </div>
            {dueDate && (
              <div
                className={
                  isOverdue ? styles.dueDatePill : styles.dueDatePillNeutral
                }
              >
                <span
                  className={
                    isOverdue
                      ? styles.dueDatePillText
                      : styles.dueDatePillTextNeutral
                  }
                >
                  Due on: <strong>{moment(dueDate).format('MMM D')}</strong>
                </span>
              </div>
            )}
          </div>
        }
      />
    </RBACProvider>
  );
}

export default FeedbackSlideOutHeader;
